import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { createOperationStudent } from '../../services/operationStudentService';
import { Save, Trash2, Search, Download, Plus } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useAuth } from '../../contexts/AuthContext';
import { useTablePreferences } from '../../hooks/useTablePreferences';

interface ArchitectureTableProps {
    programType?: string;
}

interface ArchitectureStudent {
    id: string;
    first_name: string;
    last_name: string;
    notes?: string;
    kesin_tercih?: string;
    universitaly_data: {
        username?: string;
        password?: string;
        program?: string;
    };
    documents_status: {
        language_cert?: string;
        missing_documents?: string;
        passport_status?: string;
    };
    polito_data: {
        username?: string;
        password?: string;
        exam_date?: string;
        score?: string;
        payment_status?: string;
        result?: string;
        final_payment?: string;
    };
    polimi_data: {
        username?: string;
        password?: string;
        exam_date?: string;
        score?: string;
        payment_status?: string;
        result?: string;
        cisia_username?: string;
        cisia_password?: string;
        final_payment?: string;
    };
}

const ArchitectureTable: React.FC<ArchitectureTableProps> = () => {
    const { user } = useAuth();
    const [students, setStudents] = useState<ArchitectureStudent[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Add Student State
    // Add Student State
    const [newStudent, setNewStudent] = useState({
        first_name: '',
        last_name: '',
        program: 'Politecnico di Torino',
        notes: '',
        kesin_tercih: '',
        universitaly_data: { username: '', password: '', program: '' },
        documents_status: { language_cert: '', missing_documents: '', passport_status: '' },
        polito_data: { username: '', password: '', exam_date: '', score: '', payment_status: '', result: '', final_payment: '' },
        polimi_data: { username: '', password: '', exam_date: '', score: '', payment_status: '', result: '', cisia_username: '', cisia_password: '', final_payment: '' }
    });
    const [isAdding, setIsAdding] = useState(false);
    const [availableUniversities, setAvailableUniversities] = useState<{ id: string; name: string }[]>([]);

    // Column Resizing Logic
    const { columnWidths, setColumnWidths } = useTablePreferences('architecture-student-list', {
        first_name: 120,
        last_name: 120,
        kesin_tercih: 300,
        uni_username: 120,
        uni_password: 120,
        lang_cert: 100,
        missing_docs: 100,
        polito_username: 120,
        polito_password: 120,
        polito_date: 100,
        polito_score: 80,
        polito_pay: 100,
        polito_result: 100,
        polito_final: 100,
        polimi_username: 120,
        polimi_password: 120,
        polimi_date: 100,
        polimi_score: 80,
        polimi_pay: 100,
        polimi_result: 100,
        cisia_username: 120,
        cisia_password: 120,
        polimi_final: 100,
        passport: 100,
        notes: 150,
        actions: 100
    });

    const handleMouseDown = (e: React.MouseEvent, key: string) => {
        const startX = e.clientX;
        const startWidth = columnWidths[key];

        const handleMouseMove = (moveEvent: MouseEvent) => {
            const newWidth = Math.max(50, startWidth + (moveEvent.clientX - startX));
            setColumnWidths(prev => ({ ...prev, [key]: newWidth }));
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    useEffect(() => {
        const fetchUnis = async () => {
            const { data } = await supabase.from('universities').select('id, name').order('name');
            if (data) setAvailableUniversities(data);
        };
        fetchUnis();
    }, []);

    useEffect(() => {
        fetchStudents();
    }, []);

    const fetchStudents = async () => {
        try {
            setLoading(true);
            let query = supabase
                .from('operation_students')
                .select('*')
                .order('created_at', { ascending: false });

            if (user?.branchId) query = query.eq('branch_id', user.branchId);
            const { data: opStudents, error } = await query;

            if (error) throw error;

            const filtered = (opStudents || []).filter(s => {
                const notesLower = (s.notes || '').toLowerCase();
                const progLower = (s.universitaly_data?.program || '').toLowerCase();
                const searchKeywords = ['mimarlik', 'mimarlık', 'architecture', 'mimar'];
                return searchKeywords.some(k => notesLower.includes(k) || progLower.includes(k));
            });

            setStudents(filtered.map(s => ({
                ...s,
                universitaly_data: s.universitaly_data || {},
                documents_status: s.documents_status || {},
                polito_data: s.polito_data || {},
                polimi_data: s.polimi_data || {},
                kesin_tercih: s.kesin_tercih || ''
            })));
        } catch (error) {
            console.error('Error fetching students:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (student: ArchitectureStudent) => {
        try {
            const { error } = await supabase
                .from('operation_students')
                .update({
                    first_name: student.first_name,
                    last_name: student.last_name,
                    universitaly_data: student.universitaly_data,
                    documents_status: student.documents_status,
                    polito_data: student.polito_data,
                    polimi_data: student.polimi_data,
                    kesin_tercih: student.kesin_tercih,
                    notes: student.notes
                })
                .eq('id', student.id);

            if (error) throw error;
            alert('Kayıt güncellendi.');
        } catch (error) {
            console.error('Error saving:', error);
            alert('Kaydedilirken hata oluştu');
        }
    };

    const handleDelete = async (id: string) => {
        if (!await window.appConfirm('Bu öğrenciyi silmek istediğinize emin misiniz?')) return;
        try {
            const { error } = await supabase
                .from('operation_students')
                .delete()
                .eq('id', id);

            if (error) throw error;
            setStudents(prev => prev.filter(s => s.id !== id));
        } catch (error) {
            console.error('Error deleting:', error);
            alert('Silinirken hata oluştu');
        }
    };

    const handleAddStudent = async () => {
        if (!newStudent.first_name || !newStudent.last_name) {
            alert('Lütfen ad ve soyad giriniz.');
            return;
        }

        try {
            setIsAdding(true);

            const uniName = newStudent.program;
            const progType = 'architecture';

            // Use RPC but with specific parameters to avoid ambiguity
            const { data, error } = await createOperationStudent({
                p_first_name: newStudent.first_name,
                p_last_name: newStudent.last_name,
                p_branch_id: user?.branchId,
                p_program_type: progType,
                p_university_name: uniName,
                p_program_name: 'Architecture',
                p_created_by: user?.id,
                p_is_eu: false,
                p_universitaly_data: newStudent.universitaly_data,
                p_school_portal_data: {},
                p_exam_data: {},
                p_documents_status: newStudent.documents_status,
                p_notes: newStudent.notes,
                p_kesin_tercih: newStudent.kesin_tercih,
                p_polito_data: newStudent.polito_data,
                p_polimi_data: newStudent.polimi_data
            });

            if (error) throw error;
            if (data && !data.success) throw new Error(data.error);

            await fetchStudents();
            setNewStudent({
                first_name: '',
                last_name: '',
                program: 'Politecnico di Torino',
                notes: '',
                kesin_tercih: '',
                universitaly_data: { username: '', password: '', program: '' },
                documents_status: { language_cert: '', missing_documents: '', passport_status: '' },
                polito_data: { username: '', password: '', exam_date: '', score: '', payment_status: '', result: '', final_payment: '' },
                polimi_data: { username: '', password: '', exam_date: '', score: '', payment_status: '', result: '', cisia_username: '', cisia_password: '', final_payment: '' }
            });
            alert('Öğrenci başarıyla eklendi.');

        } catch (error: any) {
            console.error('Error adding student:', error);
            alert('Öğrenci eklenirken hata: ' + error.message);
        } finally {
            setIsAdding(false);
        }
    };

    const updateStudentState = (id: string, field: string, value: any, nestedField?: string, subField?: string) => {
        setStudents(prev => prev.map(s => {
            if (s.id !== id) return s;
            const updated = { ...s };

            if (nestedField && subField) {
                // @ts-ignore
                updated[nestedField] = { ...updated[nestedField], [subField]: value };
            } else if (!nestedField) {
                // @ts-ignore
                updated[field] = value;
            }
            return updated as ArchitectureStudent;
        }));
    };

    const exportToExcel = () => {
        const exportData = students.map(s => ({
            'Ad': s.first_name,
            'Soyad': s.last_name,
            'Kesin Tercih': s.kesin_tercih,
            'Universitaly Kullanıcı': s.universitaly_data?.username,
            'Universitaly Şifre': s.universitaly_data?.password,
            'Dil Belgesi': s.documents_status?.language_cert,
            'Eksik Belge': s.documents_status?.missing_documents,
            'Polito Kullanıcı': s.polito_data?.username,
            'Polito Şifre': s.polito_data?.password,
            'Polito Sınav Tarihi': s.polito_data?.exam_date,
            'Polito Skor': s.polito_data?.score,
            'Polito Ödeme': s.polito_data?.payment_status,
            'Polito Sonuç': s.polito_data?.result,
            'Polimi Kullanıcı': s.polimi_data?.username,
            'Polimi Şifre': s.polimi_data?.password,
            'Polimi Sınav Tarihi': s.polimi_data?.exam_date,
            'Polimi Skor': s.polimi_data?.score,
            'Polimi Ödeme': s.polimi_data?.payment_status,
            'Polimi Sonuç': s.polimi_data?.result,
            'Cisia Kullanıcı': s.polimi_data?.cisia_username,
            'Cisia Şifre': s.polimi_data?.cisia_password,
            'Pasaport': s.documents_status?.passport_status,
            'Not': s.notes
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Mimarlık Öğrencileri');
        XLSX.writeFile(wb, 'Mimarlik_Ogrencileri.xlsx');
    };

    // Helper to parse "1. Uni 2. Uni" string into array
    const parseUniversities = (str: string | undefined): string[] => {
        if (!str) return [];
        // Matches "1. UniName" or just chunks.
        // Let's assume it's just a concatenated string.
        // If it was manually entered as "1.Polimi 2.Polito", we can try to split by digit+dot?
        // Or we can just treat the whole thing as a blob if it doesn't match our format?
        // But user wants to switch back to structured data.
        // Let's try to extract university names that are in our list?
        // Or simpler: just use newlines or specific separator if we controlled it.
        // Since we are transitioning, we might overwrite manual data.
        // Let's assume the user accepts overwrite or we only parse if valid.

        // Strategy: Just split by "X. " pattern?
        // "1. Polimi 2. Polito" -> ["Polimi", "Polito"]

        const matches = str.match(/\d+\.\s*([^\d]+?)(?=(\d+\.|$))/g);
        if (matches) {
            return matches.map(m => m.replace(/^\d+\.\s*/, '').trim());
        }
        return [];
    };

    const formatUniversities = (unis: string[]) => {
        return unis.filter(u => u).map((u, i) => `${i + 1}. ${u}`).join(' ');
    };

    const renderUniversitySelector = (
        currentValue: string | undefined,
        onChange: (newValue: string) => void
    ) => {
        const selectedUnis = parseUniversities(currentValue);
        // Always add an empty slot at the end
        const slots = [...selectedUnis, ''];

        const handleSelectChange = (index: number, val: string) => {
            const newSlots = [...selectedUnis];
            if (val) {
                newSlots[index] = val; // Update or add
            } else {
                // If clearing a value (and it's not the last phantom one), remove it? 
                // Or just keep gap? Better to remove if empty?
                // For now let's just set it.
                if (index < newSlots.length) {
                    newSlots.splice(index, 1);
                }
            }

            // Reconstruct the string
            // Filter out empty strings just in case
            const valid = newSlots.filter(s => s);
            onChange(formatUniversities(valid));
        };

        return (
            <div className="flex flex-col gap-1 min-w-[200px]">
                {slots.map((uni, idx) => (
                    <div key={idx} className="flex items-center gap-1">
                        <span className="text-[10px] text-gray-500 w-4">{idx + 1}.</span>
                        <select
                            className="w-full text-[11px] p-1 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            value={uni}
                            onChange={(e) => handleSelectChange(idx, e.target.value)}
                        >
                            <option value="">Seçiniz...</option>
                            {availableUniversities.map(u => (
                                <option key={u.id} value={u.name}>{u.name}</option>
                            ))}
                        </select>
                    </div>
                ))}
            </div>
        );
    };

    const renderCell = (student: ArchitectureStudent, field: string, nestedField?: string, subField?: string) => {
        let value: any = student;
        if (nestedField) value = value[nestedField];
        if (subField) value = value?.[subField];
        else if (!nestedField) value = value[field];

        if (field === 'kesin_tercih') {
            return renderUniversitySelector(value, (newValue) => updateStudentState(student.id, field, newValue, nestedField, subField));
        }

        return (
            <input
                type="text"
                className="w-full text-[11px] p-1 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                value={value || ''}
                onChange={(e) => updateStudentState(student.id, field, e.target.value, nestedField, subField)}
            />
        );
    };

    const filteredStudents = students.filter(s =>
        `${s.first_name} ${s.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="p-8 text-center">Yükleniyor...</div>;

    const columnOrder = [
        'first_name', 'last_name', 'kesin_tercih', // Mimarlık (3)
        'uni_username', 'uni_password', // Universitaly (2)
        'lang_cert', // Dil (1)
        'missing_docs', // Eksik (1)
        'polito_username', 'polito_password', 'polito_date', 'polito_score', 'polito_pay', 'polito_result', 'polito_final', // Polito (7)
        'polimi_username', 'polimi_password', 'polimi_date', 'polimi_score', 'polimi_pay', 'polimi_result', 'cisia_username', 'cisia_password', 'polimi_final', // Polimi (9)
        'passport', // Pasaport (1)
        'notes', // Not (1)
        'actions' // İşlem (1)
    ];

    return (
        <div className="space-y-4 p-4">
            <div className="flex justify-between items-center">
                <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Öğrenci ara..."
                        className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg text-sm"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button
                    onClick={exportToExcel}
                    className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                >
                    <Download className="w-4 h-4" />
                    <span>Excel'e Aktar</span>
                </button>
            </div>

            <div className="overflow-x-auto bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800">
                <table className="w-full text-left border-collapse min-w-[2000px]" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                        {columnOrder.map(key => (
                            <col key={key} style={{ width: columnWidths[key] }} />
                        ))}
                    </colgroup>
                    <thead className="sticky top-0 z-10">
                        <tr className="bg-gray-50 dark:bg-gray-800/50 text-[10px] uppercase text-gray-600 dark:text-gray-400 font-bold">
                            <th className="p-2 border-b border-r dark:border-gray-700 text-center bg-yellow-50 dark:bg-yellow-900/10" colSpan={3}>Mimarlık Sınavına Girecek Öğrenciler</th>
                            <th className="p-2 border-b border-r dark:border-gray-700 text-center bg-blue-50 dark:bg-blue-900/10" colSpan={2}>Universitaly Bilgileri</th>

                            {/* Dil Belgesi */}
                            <th className="relative p-2 border-b border-r dark:border-gray-700 text-center overflow-hidden text-ellipsis whitespace-nowrap group" rowSpan={2} style={{ width: columnWidths['lang_cert'] }}>
                                Dil Belgesi
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'lang_cert')} />
                            </th>

                            {/* Eksik Belge */}
                            <th className="relative p-2 border-b border-r dark:border-gray-700 text-center overflow-hidden text-ellipsis whitespace-nowrap group" rowSpan={2} style={{ width: columnWidths['missing_docs'] }}>
                                Eksik Belge
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'missing_docs')} />
                            </th>

                            <th className="p-2 border-b border-r dark:border-gray-700 text-center bg-red-50 dark:bg-red-900/10" colSpan={7}>Polito Hesapları</th>
                            <th className="p-2 border-b border-r dark:border-gray-700 text-center bg-green-50 dark:bg-green-900/10" colSpan={9}>Polimi Hesapları</th>

                            {/* Pasaport Bilgisi */}
                            <th className="relative p-2 border-b border-r dark:border-gray-700 text-center overflow-hidden text-ellipsis whitespace-nowrap group" rowSpan={2} style={{ width: columnWidths['passport'] }}>
                                Pasaport Bilgisi
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'passport')} />
                            </th>

                            {/* Not */}
                            <th className="relative p-2 border-b border-r dark:border-gray-700 text-center overflow-hidden text-ellipsis whitespace-nowrap group" rowSpan={2} style={{ width: columnWidths['notes'] }}>
                                Not
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'notes')} />
                            </th>

                            {/* İşlem (Action) */}
                            <th className="relative p-2 border-b dark:border-gray-700 text-center overflow-hidden text-ellipsis whitespace-nowrap group" rowSpan={2} style={{ width: columnWidths['actions'] }}>
                                İşlem
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'actions')} />
                            </th>
                        </tr>
                        <tr className="bg-gray-50 dark:bg-gray-800/50 text-[9px] uppercase text-gray-500 dark:text-gray-500">
                            {[
                                { key: 'first_name', label: 'Ad' },
                                { key: 'last_name', label: 'Soyad' },
                                { key: 'kesin_tercih', label: 'Kesin Tercih' },
                                { key: 'uni_username', label: 'Kullanıcı Adı' },
                                { key: 'uni_password', label: 'Şifre' },
                                // Merged cells handled above, these correspond to the layout
                            ].map((col) => (
                                <th key={col.key} className="relative p-2 border-b border-r dark:border-gray-700 overflow-hidden text-ellipsis whitespace-nowrap group">
                                    {col.label}
                                    <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, col.key)} />
                                </th>
                            ))}
                            {/* Polito Subheaders */}
                            {[
                                { key: 'polito_username', label: 'Kullanıcı Adı' },
                                { key: 'polito_password', label: 'Şifre' },
                                { key: 'polito_date', label: 'Sınav Tarihi' },
                                { key: 'polito_score', label: 'Skor' },
                                { key: 'polito_pay', label: 'Polito Ödeme' },
                                { key: 'polito_result', label: 'Sonuç' },
                                { key: 'polito_final', label: 'Ödeme' },
                            ].map((col) => (
                                <th key={col.key} className="relative p-2 border-b border-r dark:border-gray-700 overflow-hidden text-ellipsis whitespace-nowrap group">
                                    {col.label}
                                    <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, col.key)} />
                                </th>
                            ))}
                            {/* Polimi Subheaders */}
                            {[
                                { key: 'polimi_username', label: 'Kullanıcı Adı' },
                                { key: 'polimi_password', label: 'Şifre' },
                                { key: 'polimi_date', label: 'Sınav Tarihi' },
                                { key: 'polimi_score', label: 'Skor' },
                                { key: 'polimi_pay', label: 'Polimi Ödeme' },
                                { key: 'polimi_result', label: 'Sonuç' },
                                { key: 'cisia_username', label: 'Cisia Kullanıcı' },
                                { key: 'cisia_password', label: 'Cisia Şifre' },
                                { key: 'polimi_final', label: 'Ödeme' },
                            ].map((col) => (
                                <th key={col.key} className="relative p-2 border-b border-r dark:border-gray-700 overflow-hidden text-ellipsis whitespace-nowrap group">
                                    {col.label}
                                    <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, col.key)} />
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {/* Add New Row */}
                        <tr className="bg-blue-50/50 dark:bg-blue-900/10 border-b border-blue-100 dark:border-blue-900/30">
                            <td className="p-2 border-r dark:border-gray-800">
                                <input placeholder="Ad" className="w-full text-[11px] p-1 border rounded" value={newStudent.first_name} onChange={e => setNewStudent({ ...newStudent, first_name: e.target.value })} />
                            </td>
                            <td className="p-2 border-r dark:border-gray-800">
                                <input placeholder="Soyad" className="w-full text-[11px] p-1 border rounded" value={newStudent.last_name} onChange={e => setNewStudent({ ...newStudent, last_name: e.target.value })} />
                            </td>
                            <td className="p-2 border-r dark:border-gray-800">
                                {renderUniversitySelector(newStudent.kesin_tercih, (val) => setNewStudent({ ...newStudent, kesin_tercih: val }))}
                            </td>
                            <td className="p-2 border-r dark:border-gray-800">
                                <input placeholder="Uni K.Adı" className="w-full text-[11px] p-1 border rounded" value={newStudent.universitaly_data.username} onChange={e => setNewStudent({ ...newStudent, universitaly_data: { ...newStudent.universitaly_data, username: e.target.value } })} />
                            </td>
                            <td className="p-2 border-r dark:border-gray-800">
                                <input placeholder="Uni Şifre" className="w-full text-[11px] p-1 border rounded" value={newStudent.universitaly_data.password} onChange={e => setNewStudent({ ...newStudent, universitaly_data: { ...newStudent.universitaly_data, password: e.target.value } })} />
                            </td>
                            <td className="p-2 border-r dark:border-gray-800">
                                <input placeholder="Dil Belg." className="w-full text-[11px] p-1 border rounded" value={newStudent.documents_status.language_cert} onChange={e => setNewStudent({ ...newStudent, documents_status: { ...newStudent.documents_status, language_cert: e.target.value } })} />
                            </td>
                            <td className="p-2 border-r dark:border-gray-800">
                                <input placeholder="Eksik" className="w-full text-[11px] p-1 border rounded" value={newStudent.documents_status.missing_documents} onChange={e => setNewStudent({ ...newStudent, documents_status: { ...newStudent.documents_status, missing_documents: e.target.value } })} />
                            </td>

                            {/* Polito */}
                            <td className="p-2 border-r dark:border-gray-800 bg-red-50/20"><input placeholder="Polito K." className="w-full text-[11px] p-1 border rounded" value={newStudent.polito_data.username} onChange={e => setNewStudent({ ...newStudent, polito_data: { ...newStudent.polito_data, username: e.target.value } })} /></td>
                            <td className="p-2 border-r dark:border-gray-800 bg-red-50/20"><input placeholder="Polito Ş." className="w-full text-[11px] p-1 border rounded" value={newStudent.polito_data.password} onChange={e => setNewStudent({ ...newStudent, polito_data: { ...newStudent.polito_data, password: e.target.value } })} /></td>
                            <td className="p-2 border-r dark:border-gray-800"><input placeholder="Tarih" className="w-full text-[11px] p-1 border rounded" value={newStudent.polito_data.exam_date} onChange={e => setNewStudent({ ...newStudent, polito_data: { ...newStudent.polito_data, exam_date: e.target.value } })} /></td>
                            <td className="p-2 border-r dark:border-gray-800"><input placeholder="Skor" className="w-full text-[11px] p-1 border rounded" value={newStudent.polito_data.score} onChange={e => setNewStudent({ ...newStudent, polito_data: { ...newStudent.polito_data, score: e.target.value } })} /></td>
                            <td className="p-2 border-r dark:border-gray-800"><input placeholder="Ödeme" className="w-full text-[11px] p-1 border rounded" value={newStudent.polito_data.payment_status} onChange={e => setNewStudent({ ...newStudent, polito_data: { ...newStudent.polito_data, payment_status: e.target.value } })} /></td>
                            <td className="p-2 border-r dark:border-gray-800"><input placeholder="Sonuç" className="w-full text-[11px] p-1 border rounded" value={newStudent.polito_data.result} onChange={e => setNewStudent({ ...newStudent, polito_data: { ...newStudent.polito_data, result: e.target.value } })} /></td>
                            <td className="p-2 border-r dark:border-gray-800"><input placeholder="Final Ö." className="w-full text-[11px] p-1 border rounded" value={newStudent.polito_data.final_payment} onChange={e => setNewStudent({ ...newStudent, polito_data: { ...newStudent.polito_data, final_payment: e.target.value } })} /></td>

                            {/* Polimi */}
                            <td className="p-2 border-r dark:border-gray-800 bg-green-50/20"><input placeholder="Polimi K." className="w-full text-[11px] p-1 border rounded" value={newStudent.polimi_data.username} onChange={e => setNewStudent({ ...newStudent, polimi_data: { ...newStudent.polimi_data, username: e.target.value } })} /></td>
                            <td className="p-2 border-r dark:border-gray-800 bg-green-50/20"><input placeholder="Polimi Ş." className="w-full text-[11px] p-1 border rounded" value={newStudent.polimi_data.password} onChange={e => setNewStudent({ ...newStudent, polimi_data: { ...newStudent.polimi_data, password: e.target.value } })} /></td>
                            <td className="p-2 border-r dark:border-gray-800"><input placeholder="Tarih" className="w-full text-[11px] p-1 border rounded" value={newStudent.polimi_data.exam_date} onChange={e => setNewStudent({ ...newStudent, polimi_data: { ...newStudent.polimi_data, exam_date: e.target.value } })} /></td>
                            <td className="p-2 border-r dark:border-gray-800"><input placeholder="Skor" className="w-full text-[11px] p-1 border rounded" value={newStudent.polimi_data.score} onChange={e => setNewStudent({ ...newStudent, polimi_data: { ...newStudent.polimi_data, score: e.target.value } })} /></td>
                            <td className="p-2 border-r dark:border-gray-800"><input placeholder="Ödeme" className="w-full text-[11px] p-1 border rounded" value={newStudent.polimi_data.payment_status} onChange={e => setNewStudent({ ...newStudent, polimi_data: { ...newStudent.polimi_data, payment_status: e.target.value } })} /></td>
                            <td className="p-2 border-r dark:border-gray-800"><input placeholder="Sonuç" className="w-full text-[11px] p-1 border rounded" value={newStudent.polimi_data.result} onChange={e => setNewStudent({ ...newStudent, polimi_data: { ...newStudent.polimi_data, result: e.target.value } })} /></td>
                            <td className="p-2 border-r dark:border-gray-800"><input placeholder="Cisia K." className="w-full text-[11px] p-1 border rounded" value={newStudent.polimi_data.cisia_username} onChange={e => setNewStudent({ ...newStudent, polimi_data: { ...newStudent.polimi_data, cisia_username: e.target.value } })} /></td>
                            <td className="p-2 border-r dark:border-gray-800"><input placeholder="Cisia Ş." className="w-full text-[11px] p-1 border rounded" value={newStudent.polimi_data.cisia_password} onChange={e => setNewStudent({ ...newStudent, polimi_data: { ...newStudent.polimi_data, cisia_password: e.target.value } })} /></td>
                            <td className="p-2 border-r dark:border-gray-800"><input placeholder="Final Ö." className="w-full text-[11px] p-1 border rounded" value={newStudent.polimi_data.final_payment} onChange={e => setNewStudent({ ...newStudent, polimi_data: { ...newStudent.polimi_data, final_payment: e.target.value } })} /></td>

                            <td className="p-2 border-r dark:border-gray-800">
                                <input placeholder="Pasaport" className="w-full text-[11px] p-1 border rounded" value={newStudent.documents_status.passport_status} onChange={e => setNewStudent({ ...newStudent, documents_status: { ...newStudent.documents_status, passport_status: e.target.value } })} />
                            </td>
                            <td className="p-2 border-r dark:border-gray-800">
                                <input placeholder="Not" className="w-full text-[11px] p-1 border rounded" value={newStudent.notes} onChange={e => setNewStudent({ ...newStudent, notes: e.target.value })} />
                            </td>

                            <td className="p-2 text-center">
                                <button onClick={handleAddStudent} disabled={isAdding} className="bg-blue-600 text-white p-1 rounded hover:bg-blue-700 disabled:opacity-50" title="Ekle">
                                    <Plus size={16} />
                                </button>
                            </td>
                        </tr>
                        {filteredStudents.map((student, idx) => (
                            <tr key={student.id} className={`hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors ${idx % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50/30 dark:bg-gray-800/10'}`}>
                                <td className="p-2 border-r dark:border-gray-800">
                                    <input
                                        className="w-full text-[11px] p-1 border rounded bg-white"
                                        value={student.first_name}
                                        onChange={(e) => updateStudentState(student.id, 'first_name', e.target.value)}
                                    />
                                </td>
                                <td className="p-2 border-r dark:border-gray-800">
                                    <input
                                        className="w-full text-[11px] p-1 border rounded bg-white"
                                        value={student.last_name}
                                        onChange={(e) => updateStudentState(student.id, 'last_name', e.target.value)}
                                    />
                                </td>
                                <td className="p-2 border-r dark:border-gray-800">{renderCell(student, 'kesin_tercih')}</td>

                                <td className="p-2 border-r dark:border-gray-800 bg-blue-50/20 dark:bg-blue-900/5">{renderCell(student, '', 'universitaly_data', 'username')}</td>
                                <td className="p-2 border-r dark:border-gray-800 bg-blue-50/20 dark:bg-blue-900/5">{renderCell(student, '', 'universitaly_data', 'password')}</td>

                                <td className="p-2 border-r dark:border-gray-800">{renderCell(student, '', 'documents_status', 'language_cert')}</td>
                                <td className="p-2 border-r dark:border-gray-800">{renderCell(student, '', 'documents_status', 'missing_documents')}</td>

                                {/* Polito */}
                                <td className="p-2 border-r dark:border-gray-800 bg-red-50/20 dark:bg-red-900/5">{renderCell(student, '', 'polito_data', 'username')}</td>
                                <td className="p-2 border-r dark:border-gray-800 bg-red-50/20 dark:bg-red-900/5">{renderCell(student, '', 'polito_data', 'password')}</td>
                                <td className="p-2 border-r dark:border-gray-800">{renderCell(student, '', 'polito_data', 'exam_date')}</td>
                                <td className="p-2 border-r dark:border-gray-800">{renderCell(student, '', 'polito_data', 'score')}</td>
                                <td className="p-2 border-r dark:border-gray-800">{renderCell(student, '', 'polito_data', 'payment_status')}</td>
                                <td className="p-2 border-r dark:border-gray-800">{renderCell(student, '', 'polito_data', 'result')}</td>
                                <td className="p-2 border-r dark:border-gray-800">{renderCell(student, '', 'polito_data', 'final_payment')}</td>

                                {/* Polimi */}
                                <td className="p-2 border-r dark:border-gray-800 bg-green-50/20 dark:bg-green-900/5">{renderCell(student, '', 'polimi_data', 'username')}</td>
                                <td className="p-2 border-r dark:border-gray-800 bg-green-50/20 dark:bg-green-900/5">{renderCell(student, '', 'polimi_data', 'password')}</td>
                                <td className="p-2 border-r dark:border-gray-800">{renderCell(student, '', 'polimi_data', 'exam_date')}</td>
                                <td className="p-2 border-r dark:border-gray-800">{renderCell(student, '', 'polimi_data', 'score')}</td>
                                <td className="p-2 border-r dark:border-gray-800">{renderCell(student, '', 'polimi_data', 'payment_status')}</td>
                                <td className="p-2 border-r dark:border-gray-800">{renderCell(student, '', 'polimi_data', 'result')}</td>
                                <td className="p-2 border-r dark:border-gray-800">{renderCell(student, '', 'polimi_data', 'cisia_username')}</td>
                                <td className="p-2 border-r dark:border-gray-800">{renderCell(student, '', 'polimi_data', 'cisia_password')}</td>
                                <td className="p-2 border-r dark:border-gray-800">{renderCell(student, '', 'polimi_data', 'final_payment')}</td>

                                <td className="p-2 border-r dark:border-gray-800">{renderCell(student, '', 'documents_status', 'passport_status')}</td>
                                <td className="p-2 border-r dark:border-gray-800 min-w-[200px]">{renderCell(student, 'notes')}</td>

                                <td className="p-2 text-center">
                                    <div className="flex items-center justify-center gap-2">
                                        <button
                                            onClick={() => handleSave(student)}
                                            className="p-1 px-2 text-green-600 hover:bg-green-100 rounded transition-colors"
                                            title="Kaydet"
                                        >
                                            <Save size={16} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(student.id)}
                                            className="p-1 px-2 text-red-600 hover:bg-red-100 rounded transition-colors"
                                            title="Sil"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ArchitectureTable;
